// Two backends answer this app, so there are two clients.
//
// `api` (the default export) reaches apps/web, which serves everything outside
// the live lesson — lessons, classrooms, curriculum, analytics, spaces, exit
// tickets, accounts, trials, the SSE stream — the large majority of the app,
// which is why it holds the short name and the default export.
//
// `tutorApi` reaches apps/tutor, which owns the live lesson. Use it only for
// /session/* — anything that reads or mutates the session held in that
// service's memory. The WebSocket has its own base URL (VITE_WS_BASE_URL) and
// is built in useWebsocketClient.
//
// Sending a request to the wrong one gives a 404, not a fallback: neither
// service serves the other's paths.
import axios, { AxiosInstance } from "axios";
import { TOKEN_KEY, ACTING_TEACHER_KEY } from "@/constants/auth";
import { currentAsOf } from "@/utils/as-of";
import { TUTOR_BASE_URL, WEB_BASE_URL } from "./apiBaseUrls";

declare module "axios" {
  interface InternalAxiosRequestConfig {
    skipAuthRedirect?: boolean;
    _authRetried?: boolean;
  }
  interface AxiosRequestConfig {
    skipAuthRedirect?: boolean;
    _authRetried?: boolean;
  }
}

// Both hosts are resolved in one place; see utils/apiBaseUrls for why a
// missing variable fails the build rather than falling back to localhost.
// No fallback from web to tutor on purpose either: tutor no longer serves
// these paths, so it would turn a missing variable into a 404 that reads like
// a deleted endpoint.

const api: AxiosInstance = axios.create({
  baseURL: WEB_BASE_URL,
  withCredentials: true, // Send cookies with requests (BetterAuth session)
});

const tutorApi: AxiosInstance = axios.create({
  baseURL: TUTOR_BASE_URL,
  withCredentials: true,
});

// Create the mock API instance by transforming the URL
const mockApi: AxiosInstance = axios.create({
  baseURL: TUTOR_BASE_URL.replace("/v1", "/mock"),
  withCredentials: true,
});

// Add interceptors to every instance
[api, tutorApi, mockApi].forEach((instance) => {
  // Add a request interceptor to add headers
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const demoClientId = sessionStorage.getItem("demoClientId");
      if (demoClientId) {
        config.headers["demo-client-id"] = demoClientId;
      }
      // View-as: scope reads/assigns to the teacher the caller switched into.
      // The backend re-validates this against the caller's accessible set on
      // every request, so a stale/forged id can only ever 403, never escalate.
      // QA date simulation rides the address bar, so it survives navigation and
      // a pasted URL reproduces itself. Forwarded from the page URL to every API
      // call rather than being wired per-request: the teacher roster and the
      // student schedule are separate calls that must agree on the date, and the
      // writer runs inside them. Ignored server-side on production.
      const asOf = currentAsOf();
      if (asOf) {
        config.params = { ...(config.params ?? {}), as_of: asOf };
      }
      const actingTeacherId = localStorage.getItem(ACTING_TEACHER_KEY);
      if (actingTeacherId) {
        config.headers["X-Act-As-Teacher-Id"] = actingTeacherId;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // Add a response interceptor to handle errors
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401 && !error.config?.skipAuthRedirect) {
        // Retry once — transient BetterAuth blips resolve within seconds.
        // Only redirect to login if the retry also fails with 401.
        if (!error.config?._authRetried) {
          error.config._authRetried = true;
          error.config.skipAuthRedirect = true;
          await new Promise((r) => setTimeout(r, 500));
          try {
            return await instance.request(error.config);
          } catch (retryError: unknown) {
            if (
              retryError &&
              typeof retryError === "object" &&
              "response" in retryError &&
              (retryError as { response?: { status?: number } }).response
                ?.status !== 401
            ) {
              return Promise.reject(retryError);
            }
            // Retry also got 401 — session is genuinely dead
          }
        }
        // Lazy-load the store so auth store startup can import this API module.
        const { useAuthStore } = await import("@/store/useAuthStore");
        await useAuthStore.getState().clearAuth({ skipServerSignOut: true });
        window.location.href = "/login";
      }
      return Promise.reject(error);
    },
  );
});

export { api as default, tutorApi, mockApi };
